import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Expand,
  Link,
  Unlink,
  Copy,
  Camera,
  Clock,
  Trash2,
  Server,
  Lock,
  LockOpen,
  HardDrive,
} from 'lucide-react';
import type { Volume, VolumeActivityLog } from '@/service/gpu-instance/volume-type';
import { useVolumeActivityLogs } from '@/service/gpu-instance/volume-hook';
import {
  VolumeUsageChart,
  ResizeVolumeDialog,
  AttachVolumeDialog,
  DeleteVolumeDialog,
  CloneVolumeDialog,
  CreateSnapshotDialog,
  ScheduleSnapshotDialog,
} from '../../../components/volume';

interface OutletContextType {
  volume: Volume | undefined;
  isLoading: boolean;
}

const actionIcons: Record<string, typeof Server> = {
  created: HardDrive,
  resized: Expand,
  attached: Link,
  detached: Unlink,
  snapshot_created: Camera,
  snapshot_restored: Clock,
  cloned: Copy,
  deleted: Trash2,
  health_changed: Server,
};

const VolumeDetailOverview = () => {
  const { volume, isLoading } = useOutletContext<OutletContextType>();

  // Dialog states
  const [resizeOpen, setResizeOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  // Fetch activity logs
  const { data: activityLogs = [] } = useVolumeActivityLogs(volume?.id || '');

  if (isLoading || !volume) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-64 w-full" />
        ))}
      </div>
    );
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setSnapshotOpen(true)}>
              <Camera className="mr-2 h-4 w-4" />
              Create Snapshot
            </Button>
            <Button variant="outline" size="sm" onClick={() => setScheduleOpen(true)}>
              <Clock className="mr-2 h-4 w-4" />
              Schedule Snapshots
            </Button>
            <Button variant="outline" size="sm" onClick={() => setResizeOpen(true)}>
              <Expand className="mr-2 h-4 w-4" />
              Resize
            </Button>
            {!volume.isAttached ? (
              <Button variant="outline" size="sm" onClick={() => setAttachOpen(true)}>
                <Link className="mr-2 h-4 w-4" />
                Attach
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled>
                <Unlink className="mr-2 h-4 w-4" />
                Detach
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setCloneOpen(true)}>
              <Copy className="mr-2 h-4 w-4" />
              Clone
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteOpen(true)}
              disabled={volume.isAttached}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Usage Chart */}
        <VolumeUsageChart volume={volume} />

        {/* Volume Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Volume Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">PVC Name</span>
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{volume.pvcName}</code>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">PV Name</span>
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{volume.pvName || 'Pending'}</code>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Namespace</span>
                <span>{volume.namespace}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Access Mode</span>
                <Badge variant="outline" className="font-mono text-xs">
                  {volume.accessMode}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Volume Mode</span>
                <span>{volume.volumeMode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Encryption</span>
                <span className="flex items-center gap-1">
                  {volume.encrypted ? (
                    <>
                      <Lock className="h-3 w-3 text-green-500" />
                      Enabled
                    </>
                  ) : (
                    <>
                      <LockOpen className="h-3 w-3 text-muted-foreground" />
                      Disabled
                    </>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Trident Backend</span>
                <span className="text-xs">{volume.tridentBackend}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{formatDate(volume.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last Updated</span>
                <span>{formatRelativeTime(volume.updatedAt)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Attached Instances */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Attached Instances</CardTitle>
            <CardDescription>
              {volume.attachedInstances.length} instance
              {volume.attachedInstances.length !== 1 ? 's' : ''} connected
            </CardDescription>
          </CardHeader>
          <CardContent>
            {volume.attachedInstances.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Server className="h-8 w-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No instances attached</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setAttachOpen(true)}
                >
                  Attach to Instance
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {volume.attachedInstances.map((inst) => (
                  <div
                    key={inst.instanceId}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-3">
                      <Server className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm">{inst.instanceName}</p>
                        <p className="text-xs text-muted-foreground">
                          Mount: <code className="bg-muted px-1 rounded">{inst.mountPath}</code>
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(inst.attachedAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Labels */}
        {Object.keys(volume.labels).length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Labels</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(volume.labels).map(([key, value]) => (
                  <Badge key={key} variant="secondary" className="text-xs">
                    {key}: {value}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Activity Log */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Activity Log</CardTitle>
          <CardDescription>Recent actions and events for this volume</CardDescription>
        </CardHeader>
        <CardContent>
          {activityLogs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No activity recorded yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activityLogs.slice(0, 10).map((log) => {
                  const Icon = actionIcons[log.action] || Server;
                  return (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span className="capitalize">{log.action.replace(/_/g, ' ')}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{log.description}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {log.userName || 'System'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatRelativeTime(log.timestamp)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ResizeVolumeDialog
        open={resizeOpen}
        onOpenChange={setResizeOpen}
        volume={volume}
      />
      <AttachVolumeDialog
        open={attachOpen}
        onOpenChange={setAttachOpen}
        volume={volume}
      />
      <DeleteVolumeDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        volume={volume}
      />
      <CloneVolumeDialog
        open={cloneOpen}
        onOpenChange={setCloneOpen}
        volume={volume}
      />
      <CreateSnapshotDialog
        open={snapshotOpen}
        onOpenChange={setSnapshotOpen}
        volume={volume}
      />
      <ScheduleSnapshotDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        volume={volume}
      />
    </div>
  );
};

export default VolumeDetailOverview;
